/**
 * PostHog for the public marketing site (build F16).
 *
 * - Loads only when `VITE_POSTHOG_KEY` is set; otherwise every call is a silent no-op.
 * - US cloud. Pageviews are captured automatically on SPA navigation.
 * - Session replay runs on public pages and `/signup` only, with every input masked.
 * - Nothing is captured on logged-in or member-facing paths: events from those
 *   paths are dropped in `before_send`, and replay stops as soon as the visitor
 *   navigates there. Never pass member data, message bodies, door codes or
 *   financial details to `track`.
 *
 * The SDK is imported lazily so visitors without a key (and the app bundle's
 * critical path) never download it.
 *
 * One PostHog instance serves the whole SPA. The logged-in app (backend build
 * session, B10) must not call `posthog.init` itself: it calls `getPosthog()`
 * for the shared client and `registerPosthogPaths()` to opt app paths into
 * capture and/or replay. Replay is always started/stopped explicitly here
 * (`disable_session_recording: true` at init), so it never runs on a path no
 * policy allows.
 */
import type { PostHog } from "posthog-js";

export type MarketingEvent =
  | "cta_clicked"
  | "pricing_estimator_used"
  | "savings_calculator_used"
  | "syre_claim_clicked"
  | "syre_webinar_played"
  | "syre_tier_selected"
  | "syre_presentation_clicked"
  | "syre_qa_clicked"
  | "syre_bonus_impression"
  | "signup_started"
  | "signup_submitted";

export const POSTHOG_API_HOST = "https://us.i.posthog.com";
export const POSTHOG_UI_HOST = "https://us.posthog.com";

/** Path prefixes that are never tracked: the logged-in app, admin and member/vendor/tenant-facing tools. */
const UNTRACKED_PREFIXES = [
  "/app",
  "/admin",
  "/vendor-portal",
  "/service-form",
  "/cleaning",
  "/booking-request",
  "/rooms-for-rent",
  "/owners",
  "/member",
];

export function isMarketingPath(pathname: string): boolean {
  const p = (pathname || "/").toLowerCase();
  return !UNTRACKED_PREFIXES.some((prefix) => p === prefix || p.startsWith(`${prefix}/`));
}

function posthogKey(): string | undefined {
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  return key && key.trim() ? key.trim() : undefined;
}

let client: PostHog | null = null;
let loading: Promise<PostHog | null> | null = null;
const pending: Array<{ event: MarketingEvent; props?: Record<string, unknown> }> = [];
const MAX_PENDING = 50;

function pathOf(url: unknown): string {
  if (typeof url !== "string") return typeof window !== "undefined" ? window.location.pathname : "/";
  try {
    return new URL(url).pathname;
  } catch {
    return "/";
  }
}

export interface PosthogPathPolicy {
  /** Non-marketing paths whose events may be sent (e.g. app routes after identify). */
  capture?: (pathname: string) => boolean;
  /** Non-marketing paths where session replay may run (inputs are always masked). */
  record?: (pathname: string) => boolean;
}

const policies: PosthogPathPolicy[] = [];

/** Marketing paths always capture; other paths only when a registered policy allows it. */
export function captureAllowed(pathname: string): boolean {
  return isMarketingPath(pathname) || policies.some((p) => p.capture?.(pathname) === true);
}

/** Marketing paths always record; other paths only when a registered policy allows it. */
export function recordAllowed(pathname: string): boolean {
  return isMarketingPath(pathname) || policies.some((p) => p.record?.(pathname) === true);
}

/** Lets the logged-in app opt its own paths into capture and/or replay. */
export function registerPosthogPaths(policy: PosthogPathPolicy): void {
  policies.push(policy);
  syncRecording();
}

/** Start or stop replay to match the current path. */
function syncRecording(): void {
  if (!client || typeof window === "undefined") return;
  if (recordAllowed(window.location.pathname)) {
    if (!client.sessionRecordingStarted()) client.startSessionRecording();
  } else if (client.sessionRecordingStarted()) {
    client.stopSessionRecording();
  }
}

/** Initializes PostHog once, on a marketing path, when a key is configured. */
export function initMarketingAnalytics(): Promise<PostHog | null> {
  if (typeof window === "undefined" || !isMarketingPath(window.location.pathname)) {
    return loading ?? Promise.resolve(null);
  }
  return getPosthog();
}

/**
 * The shared PostHog client on any path (null without `VITE_POSTHOG_KEY`).
 * Initializes it once if nothing has yet. For use by the logged-in app.
 */
export function getPosthog(): Promise<PostHog | null> {
  if (loading) return loading;
  const key = posthogKey();
  if (!key || typeof window === "undefined") return Promise.resolve(null);
  loading = import("posthog-js")
    .then(({ default: posthog }) => {
      posthog.init(key, {
        api_host: POSTHOG_API_HOST,
        ui_host: POSTHOG_UI_HOST,
        capture_pageview: "history_change",
        capture_pageleave: true,
        autocapture: false,
        person_profiles: "identified_only",
        session_recording: {
          maskAllInputs: true,
          // In-app replay (backend B10) masks member/financial areas with `ph-mask`;
          // text masking doesn't hide media, so block it there too.
          blockSelector:
            ".ph-mask img, .ph-mask picture, .ph-mask video, .ph-mask canvas, .ph-mask iframe, .ph-mask svg image",
        },
        disable_session_recording: true,
        before_send: (event) => {
          if (!event) return null;
          return captureAllowed(pathOf(event.properties?.$current_url)) ? event : null;
        },
      });
      client = posthog;
      for (const name of ["pushState", "replaceState", "popstate"]) {
        window.addEventListener(name, syncRecording);
      }
      syncRecording();
      for (const { event, props } of pending.splice(0)) posthog.capture(event, props);
      return posthog;
    })
    .catch(() => null);
  return loading;
}

/** Captures a marketing event (no-op without a key or off a marketing path). */
export function track(event: MarketingEvent, props?: Record<string, unknown>): void {
  if (!posthogKey() || typeof window === "undefined") return;
  if (!isMarketingPath(window.location.pathname)) return;
  if (client) {
    client.capture(event, props);
    return;
  }
  if (pending.length < MAX_PENDING) pending.push({ event, props });
  void initMarketingAnalytics();
}
